import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

const navLinks = [
  { label: "Features", href: "#features" },
  { label: "Pricing", href: "#pricing" },
  { label: "Services", href: "#services" },
  { label: "About", href: "#about" },
  { label: "Contact", href: "#contact" },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollTo = (href) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
    setMenuOpen(false);
  };

  return (
    <>
      <motion.nav
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="fixed top-0 left-0 right-0 z-[999] px-[5%] transition-all duration-300"
        style={{
          background: scrolled
            ? "rgba(5,13,26,0.95)"
            : "rgba(5,13,26,0.7)",
          backdropFilter: "blur(14px)",
          borderBottom: "1px solid rgba(255,255,255,0.06)",
        }}
        role="navigation"
        aria-label="Main navigation"
      >
        <div className="flex items-center justify-between h-[68px] max-w-[1200px] mx-auto">
          {/* Logo */}
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="font-display font-extrabold text-[1.35rem] tracking-tight gradient-text bg-none border-none cursor-pointer"
            aria-label="Tekstation home"
          >
            Tekstation
          </button>

          {/* Desktop Links */}
          <ul className="hidden md:flex items-center gap-8 list-none">
            {navLinks.map((link) => (
              <li key={link.href}>
                <button
                  onClick={() => scrollTo(link.href)}
                  className="text-[0.88rem] font-medium uppercase tracking-wide transition-colors duration-200 bg-none border-none cursor-pointer"
                  style={{ color: "var(--slate)" }}
                  onMouseEnter={(e) => (e.target.style.color = "var(--white)")}
                  onMouseLeave={(e) => (e.target.style.color = "var(--slate)")}
                >
                  {link.label}
                </button>
              </li>
            ))}
          </ul>

          {/* CTA */}
          <button
            className="hidden md:block btn-primary text-sm"
            onClick={() => scrollTo("#contact")}
            aria-label="Get started with Tekstation"
          >
            Get Started
          </button>

          {/* Hamburger */}
          <button
            className="md:hidden flex flex-col gap-[5px] bg-none border-none cursor-pointer p-1"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle mobile menu"
            aria-expanded={menuOpen}
          >
            {[0, 1, 2].map((i) => (
              <span
                key={i}
                className="block w-6 h-[2px] rounded-sm transition-all duration-300"
                style={{
                  background: "var(--slate)",
                  transform:
                    menuOpen
                      ? i === 0
                        ? "rotate(45deg) translate(5px, 5px)"
                        : i === 2
                        ? "rotate(-45deg) translate(5px, -5px)"
                        : "opacity: 0"
                      : "none",
                  opacity: menuOpen && i === 1 ? 0 : 1,
                }}
              />
            ))}
          </button>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.25 }}
            className="fixed top-[68px] inset-x-0 z-[998] flex flex-col gap-6 px-[5%] py-8"
            style={{ background: "var(--navy2)" }}
            role="dialog"
            aria-label="Mobile navigation"
          >
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => scrollTo(link.href)}
                className="text-left text-lg font-medium bg-none border-none cursor-pointer transition-colors duration-200"
                style={{ color: "var(--steel)" }}
              >
                {link.label}
              </button>
            ))}
            <button
              className="btn-primary text-center mt-2"
              onClick={() => scrollTo("#contact")}
            >
              Get Started
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
